import * as Yup from "yup";
import { EMAIL_REGEX, MOBILE_REGEX, NOSPACE_REGEX } from "./constants";
import { MESSAGE } from "./validationMessage";

export let email = Yup.string()
  .max(60, "Email should not be longer than 60 characters")
  .required(MESSAGE.REQUIRED)
  .email(MESSAGE.EMAIL)
  .matches(EMAIL_REGEX, MESSAGE.EMAIL);

let name = Yup.string()
  .max(60, "Name should not be longer than 60 characters")
  .required(MESSAGE.REQUIRED)
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE);

let title = Yup.string()
  .max(60, "This field should not be longer than 60 characters")
  .required(MESSAGE.REQUIRED)
  .matches(/^[a-zA-Z0-9 ]+$/, "Only letters and numbers are allowed. No special characters.")
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE);

let description = Yup.string()
  .max(250, "Description should not be longer than 250 characters")
  .required(MESSAGE.REQUIRED)
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE);

let question = Yup.string()
  .max(150, "This field should not be longer than 150 characters")
  .required(MESSAGE.REQUIRED)
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE);

let status = Yup.mixed().required(MESSAGE.REQUIRED);

let mobile = Yup.string().required('Phone Number is required')
  .matches(MOBILE_REGEX, "Please Enter a valid number")
  .test('sum-not-zero', 'Please Enter a valid number', value => {
    if (value) {
      const sum = value.split('').reduce((acc, digit) => acc + parseInt(digit, 15), 0);
      return sum !== 0;
    }
    return true;
  });

export const CmsSchema = Yup.object({
  title,
});

const ALPHANUMERIC_WITH_LETTERS_REGEX = /^(?=.*[a-zA-Z])[A-Za-z0-9]*$/;
export const CategorySchema = Yup.object().shape({
  name: Yup.string()
    .required("Category name is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(2, "Category name must be at least 2 characters"),
  image: Yup.mixed().required("Image is required"),
});

export const BidPackageSchema = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE),
  price: Yup.number()
    .typeError("Price must be a number")
    .required("Price is required")
    .min(1, "Price must be greater than 0")
    .max(10000, "Price must not exceed 10,000")
    .test("price-format", "Invalid price format", (value) => {
      return /^\d+(\.\d{1,2})?$/.test(value);
    }),
  earnBids: Yup.number()
    .typeError("Earn Bids must be a number")
    .required("Earn Bids is required")
    .min(1, "Earn Bids must be greater than 0")
    .max(10000, "Earn Bids must not exceed 10,000")
    .test("earnBids-format", "Invalid Earn Bids format", (value) => {
      return /^\d+(\.\d{1,2})?$/.test(value);
    }),
  description: Yup.array()
    .of(
      Yup.string()
        .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
        .required("Description point cannot be empty")
    )
    .min(1, "At least one description point is required"),
});

export const AdminSchema = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters")
    .matches(/^[a-zA-Z0-9 ]+$/, "Only letters and numbers are allowed. No special characters.")
    .matches(/^[^\s].*[^\s]$|^[^\s]$/, "First name should not have leading or trailing spaces."),
  email: email,
})

export const userSchema = Yup.object().shape({
  firstname: Yup.string()
    .min(2, "First name must be at least 2 characters")
    .max(30, "First name should not be longer than 30 characters")
    .matches(/^[a-zA-Z0-9 ]+$/, "Only letters and numbers are allowed. No special characters.")
    .required(MESSAGE.REQUIRED)
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE),

  lastname: Yup.string()
    .min(2, "First name must be at least 2 characters")
    .max(30, "Last name should not be longer than 30 characters")
    .matches(/^[a-zA-Z0-9 ]+$/, "Only letters and numbers are allowed. No special characters.")
    .required(MESSAGE.REQUIRED)
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE),

  username: Yup.string()
    .min(2, "Username must be at least 2 characters.")
    .max(20, "Username must be at most 20 characters.")
    .matches(/^[a-zA-Z0-9_.]+$/, "Username can only contain letters, numbers, underscores (_), and periods (.)")
    .required("This field is required."),

  email: email,
  phoneNumber: mobile,
})

function addOneMonth(date) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1);
  return d;
}

export const ContestSchema = Yup.object({
  contestName: Yup.string()
    .min(2, "Contest Name must be at least 2 characters")
    .max(100, "Contest Name should not be longer than 100 characters")
    .required(MESSAGE.REQUIRED)
    .matches(/^[a-zA-Z0-9 ]+$/, "Only letters and numbers are allowed. No special characters.")
    // .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    ,

  leagueId: Yup.string().required("League selection is required"),
  type: Yup.string().required("Type is required"),
  gameType: Yup.string().required("Game Type is required"),
  roomType: Yup.string()
    .oneOf(["limited", "unlimited"], "Invalid room type")
    .required("Room type is required"),

  totalRoomSize: Yup.string()
    .matches(/^\d+$/, "Room Size should be a number")
    .test(
      "is-required-if-limited",
      "Room size is required and must be at least 1",
      function (value) {
        return this.parent.roomType === "limited" ? !!value && Number(value) >= 1 : true;
      }
    ),

  numberOfWinners: Yup.number()
    // .required("Number of Winners is required")
    .test("required-if-not-fun", "Number of Winners is required", function (value) {
      const { gameType } = this.options.context || {};
      if (gameType === "playForFun") return true;
      return value !== null && value !== undefined && value !== "";
    })
    .min(1, "Number Of Winner must be greater than 0"),

  entryFee: Yup.number()
    .typeError("Must be a number")
    .test("entry-fee-required-if-not-fun", "Entry Fee is required", function (value) {
      const { gameType } = this.parent;
      if (gameType === "playForFun") {
        return true;
      }
      return !!value && value > 0;
    }),

  contestCommission: Yup.number()
    .typeError("Must be a number")
    .nullable(true)
    .min(1, "Commission must be greater than 0")
    .test("commission-validation", "Commission must be between 5 and 100", function (value) {
      const { gameType } = this.parent;
      if (gameType === "playForFun") return true;
      return value >= 5 && value <= 100;
    }),

  contestType: Yup.object().shape({
    type: Yup.string().required("Contest Type is required"),
    playerPosition: Yup.string().when("type", {
      is: (val) => val === "position" || val === "action",
      then: (schema) => schema.required("Player Position is required"),
    }),
    negativeType: Yup.string().when("type", {
      is: "negative-point",
      then: (schema) => schema.required("Negative Type is required"),
    }),
    playerActionType: Yup.string().when("type", {
      is: "action",
      then: (schema) => schema.required("Action Type is required"),
    }),
  }),

  payoutStructure: Yup.array()
  .of(
    Yup.object().shape({
      startPosition: Yup.number()
        .nullable(true)
        .typeError("Must be a number")
        .test("required-if-not-fun", "Start Position is required", function (value) {
          const { gameType } = this.options.context || {};
          if (gameType === "playForFun") return true;
          return value !== null && value !== undefined && value !== "";
        }),

      endPosition: Yup.number()
        .nullable(true)
        .typeError("Must be a number")
        .test("required-if-not-fun", "End Position is required", function (value) {
          const { gameType } = this.options.context || {};
          if (gameType === "playForFun") return true;
          return value !== null && value !== undefined && value !== "";
        })
        .min(Yup.ref("startPosition"), "End Position must be greater than Start Position"),

      percentage: Yup.number()
        .nullable(true)
        .typeError("Must be a number")
        .test("required-if-not-fun", "Winning Percentage is required", function (value) {
          const { gameType } = this.options.context || {};
          if (gameType === "playForFun") return true;
          return value !== null && value !== undefined && value !== "";
        })
        .min(0, "Percentage cannot be negative")
        .max(100, "Percentage cannot exceed 100"),
    })
  )
  .test("total-winners-match", "Total winners in payout structure must equal Number of Winners", function (value) {
    const { numberOfWinners, gameType } = this.options.context || {};
    if (gameType === "playForFun") return true;

    if (!Array.isArray(value)) return false;

    const totalWinners = value.reduce((acc, curr) => {
      const start = Number(curr.startPosition);
      const end = Number(curr.endPosition);
      if (!isNaN(start) && !isNaN(end)) {
        return acc + (end - start + 1);
      }
      return acc;
    }, 0);

    return totalWinners === Number(numberOfWinners);
  }),

  country: Yup.string().required("Country is required"),

  timeRange: Yup.string().required("Time Range is required"),

  startTime: Yup.string()
    .required("Start date is required")
    .test("valid-start-date", "Start data must be a valid date", value => !value || !isNaN(new Date(value).getTime())),

  endTime: Yup.string().when("timeRange", {
    is: (val) => ["season", "daily", "week", "monthly"].includes(val),
    then: (schema) =>
      schema
        .required("End Date is required")
        .test("validate-end-time", "Invalid End Date", function (value) {
          const { startTime, timeRange } = this.parent;
          if (!startTime || !value) return false;

          const start = new Date(startTime);
          const end = new Date(value);

          if (timeRange === "season") {
            const minEnd = new Date(start);
            minEnd.setMonth(minEnd.getMonth() + 1);
            return (
              end >= minEnd ||
              this.createError({ message: "End Date must be at least 1 month after Start Date" })
            );
          }

          if (timeRange === "daily") {
            return (
              start.toDateString() === end.toDateString() ||
              this.createError({
                message: "Daily contests must have the same start and end date.",
              })
            );
          }

          if (timeRange === "week") {
            const diffDays = (end - start) / (1000 * 60 * 60 * 24);
            return (
              diffDays >= 6 ||
              this.createError({
                message: "Weekly contests must be at least 7 days.",
              })
            );
          }

          if (timeRange === "monthly") {
            const diffDays = (end - start) / (1000 * 60 * 60 * 24);
            return (
              diffDays >= 28 ||
              this.createError({
                message: "Monthly contests must be at least 1 month.",
              })
            );
          }
          return true;
        }),
    otherwise: (schema) => schema.nullable(true),
  }),
});

export const JackpotSchema = Yup.object().shape({
  name: Yup.string()
    .trim('No spaces allowed')
    .strict(true)
    .required('Name is required'),

  description: Yup.string()
    .trim('No spaces allowed')
    .strict(true)
    .required('Description is required'),

  drawDate: Yup.mixed().required('Draw date is required'),
  // winDate: Yup.mixed().required('Win date is required')
  //   .test('is-before', 'Win date must be after draw date', function (value) {
  //     const { drawDate } = this.parent;
  //     if (!drawDate || !value) return true;
  //     return new Date(drawDate) <= new Date(value);
  //   }),
  status: Yup.string().required('Status is required'),
  contestId: Yup.array().min(1, 'At least one contest is required'),
  prizeType: Yup.string().required('Prize type is required'),
  money: Yup.mixed().test(
    'money-validation',
    'Invalid money data',
    function (value) {
      const { prizeType } = this.parent;
      if (prizeType !== 'money') return true;
      if (!Array.isArray(value) || value.length === 0) {
        return this.createError({ message: 'At least one money prize is required' });
      }

      for (let i = 0; i < value.length; i++) {
        const amount = value[i]?.amount;
        if (amount === undefined || amount === null || amount === '') {
          return this.createError({ path: `money[${i}].amount`, message: 'Amount is required' });
        }
        if (isNaN(amount)) {
          return this.createError({ path: `money[${i}].amount`, message: 'Amount must be a number' });
        }
      }
      return true;
    }
  ),

  imageTitles: Yup.array().when('prizeType', {
    is: 'product',
    then: (schema) =>
      schema.of(
        Yup.string().required("Image title is required")
      ).min(1, "At least one product prize is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  imageNames: Yup.array().when('prizeType', {
    is: 'product',
    then: (schema) =>
      schema.of(
        Yup.string().required("Image name is required")
      ).min(1, "At least one product prize is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const atLeastOneChecked = (checkboxes) => {
  return Array.isArray(checkboxes) && checkboxes.some((item) => item.isAllowed);
};

export const GameSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),

  budget: Yup.string().required("Budget is required"),

  ruleType: Yup.string().required("Rule Type is required"),
});